import { useMemo, useState, useRef, useCallback } from 'react';
import type { SortingState, VisibilityState, RowSelectionState, Table } from '@tanstack/react-table';
import type { GaugingRow, GaugingResponse } from '@/api/dataVault';
import { SkeletonTable } from '../components/ui/SkeletonLoader';
import { DataTable, DataTableColumnVisibility, type DataTableRef } from '../components/table';
import { exportToCSV, exportToXLSX } from '../components/table/tableExport';
import { CustomExportDialog } from '../components/table/CustomExportDialog';
import { createGaugingColumns } from './gaugingColumns';

interface Props {
  data: GaugingResponse | undefined;
  isLoading: boolean;
  page: number;
  onPageChange: (p: number) => void;
  sortBy: string;
  sortDir: 'asc' | 'desc';
  onSortChange: (col: string) => void;
  onSelectWell?: (wellId: string) => void;
}

export function GaugingTab({
  data,
  isLoading, page, onPageChange, sortBy, sortDir, onSortChange,
  onSelectWell,
}: Props) {
  const tableRef = useRef<DataTableRef<GaugingRow>>(null);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [customExportOpen, setCustomExportOpen] = useState(false);
  const [tableInstance, setTableInstance] = useState<Table<GaugingRow> | null>(null);

  // Column definitions
  const columns = useMemo(
    () => createGaugingColumns({ onSelectWell }),
    [onSelectWell],
  );

  // Bridge: parent uses sortBy/sortDir strings, DataTable uses TanStack SortingState
  const sorting: SortingState = useMemo(
    () => sortBy ? [{ id: sortBy, desc: sortDir === 'desc' }] : [],
    [sortBy, sortDir],
  );

  const handleSortingChange = useCallback((next: SortingState) => {
    if (next.length > 0) {
      onSortChange(next[0].id);
    }
  }, [onSortChange]);

  // Bridge: parent uses 1-indexed page, DataTable uses 0-indexed pageIndex
  const handlePaginationChange = useCallback((zeroIdx: number) => {
    onPageChange(zeroIdx + 1);
  }, [onPageChange]);

  // Toolbar captures the table instance
  const renderToolbar = useCallback((table: Table<GaugingRow>) => {
    if (!tableInstance) setTableInstance(table);
    return null;
  }, [tableInstance]);

  if (isLoading && !data) {
    return (
      <div className="animate-fade-in">
        <SkeletonTable rows={10} />
      </div>
    );
  }

  if (!data || data.items.length === 0) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-mono-400 text-xs tracking-wider">
          {data?.total === 0 ? 'NO GAUGING RESULTS MATCH YOUR FILTERS' : 'SELECT SITES TO VIEW DATA'}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Summary */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3" style={{ borderBottom: '1px solid transparent', borderImage: 'linear-gradient(to right, rgba(6,182,212,0.3), rgba(168,85,247,0.3)) 1' }}>
        <div className="text-xs text-mono-200 tracking-wider pb-2 truncate max-w-[200px] sm:max-w-none">
          {data.total.toLocaleString()} MEASUREMENTS
        </div>
        <div className="flex items-center gap-2 pb-2">
          <button
            onClick={() => tableInstance && exportToCSV(tableInstance, 'gauging-data')}
            className="px-2.5 py-1 text-[10px] tracking-wider text-mono-300 hover:text-mono-100 bg-mono-800 rounded transition-colors"
          >
            CSV
          </button>
          <button
            onClick={() => tableInstance && exportToXLSX(tableInstance, 'gauging-data')}
            className="px-2.5 py-1 text-[10px] tracking-wider text-mono-300 hover:text-mono-100 bg-mono-800 rounded transition-colors"
          >
            XLSX
          </button>
          <button
            onClick={() => setCustomExportOpen(true)}
            className="px-2.5 py-1 text-[10px] tracking-wider text-mono-300 hover:text-mono-100 bg-mono-800 rounded transition-colors"
          >
            CUSTOM
          </button>
          {tableInstance && (
            <DataTableColumnVisibility
              table={tableInstance}
              columnVisibility={columnVisibility}
              onColumnVisibilityChange={setColumnVisibility}
            />
          )}
        </div>
      </div>

      {/* TanStack Table */}
      <DataTable<GaugingRow>
        ref={tableRef}
        columns={columns}
        data={data.items}
        pageCount={data.total_pages}
        pageIndex={page - 1}
        pageSize={data.page_size}
        onPaginationChange={handlePaginationChange}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        enableRowSelection
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={setColumnVisibility}
        enableVirtualization
        toolbar={renderToolbar}
      />

      {/* Custom Export Dialog */}
      {tableInstance && (
        <CustomExportDialog
          open={customExportOpen}
          onClose={() => setCustomExportOpen(false)}
          table={tableInstance}
          filename="gauging-data"
        />
      )}
    </div>
  );
}
